import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Star, ExternalLink, Crown, Trophy, Award } from "lucide-react";

const mockProducts = [
  {
    title: "Sony WH-1000XM5 Wireless Noise Cancelling Headphones",
    price: "348.00",
    originalPrice: "399.99",
    discount: 13,
    currency: "$",
    rating: 4.7,
    reviews: 12843,
    image: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=300&fit=crop",
    availability: "in_stock",
    domain: "amazon.com",
    history: [298, 310, 305, 325, 315, 340, 330, 348, 345, 348],
    features: ["Industry-leading noise cancellation", "30-hour battery life", "Multipoint connection"],
  },
  {
    title: "Apple AirPods Pro (2nd Gen) with USB-C MagSafe Case",
    price: "189.99",
    originalPrice: "249.00",
    discount: 24,
    currency: "$",
    rating: 4.8,
    reviews: 48210,
    image: "https://images.unsplash.com/photo-1606220945770-b5b6c2c55bf1?w=300&h=300&fit=crop",
    availability: "in_stock",
    domain: "amazon.com",
    history: [199, 195, 189, 192, 185, 189, 195, 189, 189, 189],
    features: ["Active Noise Cancellation", "Adaptive Audio", "Personalized Spatial Audio"],
  },
  {
    title: "Logitech MX Master 3S Wireless Performance Mouse",
    price: "89.99",
    originalPrice: "99.99",
    discount: 10,
    currency: "$",
    rating: 4.6,
    reviews: 8742,
    image: "https://images.unsplash.com/photo-1527864550417-7fd91fc51a46?w=300&h=300&fit=crop",
    availability: "in_stock",
    domain: "amazon.co.uk",
    history: [99, 95, 92, 89, 92, 88, 85, 89, 89, 89],
    features: ["8K DPI optical sensor", "Quiet clicking", "USB-C fast charging"],
  },
  {
    title: "Samsung 970 EVO Plus 1TB NVMe M.2 Internal SSD",
    price: "79.99",
    originalPrice: "109.99",
    discount: 27,
    currency: "$",
    rating: 4.5,
    reviews: 34120,
    image: "https://images.unsplash.com/photo-1597872200969-2b65d56bd16b?w=300&h=300&fit=crop",
    availability: "out_of_stock",
    domain: "amazon.in",
    history: [99, 95, 92, 89, 85, 82, 79, 79, 79, 79],
    features: ["3,500 MB/s read speed", "NVMe M.2 interface", "5-year warranty"],
  },
];

function StarRating({ rating, count, compact }) {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.3;
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={compact ? 12 : 14}
          className={
            i < full
              ? "fill-[#ffa41c] text-[#ffa41c]"
              : i === full && hasHalf
              ? "fill-[#ffa41c]/50 text-[#ffa41c]"
              : "text-[#d4d4d8] fill-none"
          }
        />
      ))}
      <span className={`text-xs ml-1 ${compact ? "text-[#A1A1AA]" : "text-[#52525B]"}`}>
        {rating}/5{count ? ` (${count.toLocaleString()})` : ""}
      </span>
    </div>
  );
}

function Sparkline({ data, color = "#2563EB" }) {
  const max = Math.max(...data);
  return (
    <div className="flex items-end gap-[2px] h-6 mt-2">
      {data.map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-t min-h-[3px] transition-all"
          style={{ height: `${Math.max(12, (v / max) * 100)}%`, backgroundColor: color }}
        />
      ))}
    </div>
  );
}

function ProductCard({ product, dark }) {
  const stockClass =
    product.availability === "in_stock"
      ? dark ? "bg-[#0a3d0a] text-[#4ade80]" : "bg-[#e7f7e7] text-[#067d06]"
      : dark ? "bg-[#3d0a0a] text-[#f87171]" : "bg-[#fee2e2] text-[#b12704]";
  const stockLabel = product.availability.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <div className={dark ? "preview-product-card-dark" : "preview-product-card"} data-testid="preview-product-card">
      <div className={`absolute top-2 right-2 ${dark ? "bg-white/20" : "bg-black/70"} text-white text-[10px] px-1.5 py-0.5 rounded z-10`}>
        {product.domain}
      </div>
      <div className={`relative flex items-center justify-center p-5 aspect-square ${dark ? "bg-[#1a1a2e]" : "bg-[#f8f8f8]"}`}>
        <img
          src={product.image}
          alt={product.title}
          className="max-w-full max-h-[160px] object-contain"
          loading="lazy"
        />
      </div>
      <div className="p-4">
        <h3 className={`text-sm font-medium leading-snug mb-2 line-clamp-2 ${dark ? "text-[#e4e4e7]" : "text-[#0f1111]"}`}>
          <span className={`cursor-pointer ${dark ? "hover:text-[#ffa41c]" : "hover:text-[#c7511f]"}`}>{product.title}</span>
        </h3>
        <StarRating rating={product.rating} />
        <div className={`text-xl font-bold mt-2 ${dark ? "text-[#e4e4e7]" : "text-[#0f1111]"}`}>
          <span className="text-xs align-super">{product.currency}</span>
          {product.price}
        </div>
        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded mt-1 ${stockClass}`}>
          {stockLabel}
        </span>
        {product.history && (
          <div className={`mt-2 pt-2 border-t ${dark ? "border-[#2a2a3e]" : "border-[#eee]"}`}>
            <div className={`text-[10px] ${dark ? "text-[#71717a]" : "text-[#888]"}`}>Price History</div>
            <Sparkline data={product.history} color={dark ? "#818cf8" : "#2563EB"} />
          </div>
        )}
        <button className={`mt-3 w-full rounded-full py-2 text-sm font-semibold transition-colors cursor-pointer flex items-center justify-center gap-2 ${
          dark
            ? "bg-[#ffa41c] text-[#0f1111] hover:bg-[#ffb84d] border-0"
            : "bg-[#ffd814] text-[#0f1111] border border-[#fcd200] hover:bg-[#f7ca00]"
        }`}>
          Buy on Amazon
          <ExternalLink size={12} />
        </button>
        <p className={`text-[10px] italic mt-2 text-center ${dark ? "text-[#71717a]" : "text-[#888]"}`}>
          As an Amazon Associate, we earn from qualifying purchases.
        </p>
      </div>
    </div>
  );
}

function ComparisonTable() {
  const products = mockProducts.slice(0, 3);
  const bestValue = 1; // AirPods Pro (lowest price to value ratio)
  const topRated = 1; // AirPods Pro (highest rating)

  return (
    <div className="max-w-4xl mx-auto overflow-x-auto" data-testid="preview-comparison-table">
      <table className="w-full border-collapse border border-[#e4e4e7] rounded-lg overflow-hidden text-sm">
        <thead>
          <tr className="bg-[#F9FAFB]">
            <th className="p-3 text-left font-semibold text-[#09090B] border-b border-[#e4e4e7] w-[120px]"></th>
            {products.map((p, i) => (
              <th key={i} className={`p-3 text-center border-b border-l border-[#e4e4e7] ${i === bestValue ? "bg-[#fffbe6]" : ""}`}>
                <div className="flex flex-col items-center gap-2">
                  {i === bestValue && (
                    <span className="inline-flex items-center gap-1 bg-[#FF7300] text-white text-[10px] font-bold px-2 py-0.5 rounded">
                      <Trophy size={10} /> Best Value
                    </span>
                  )}
                  {i === topRated && i !== bestValue && (
                    <span className="inline-flex items-center gap-1 bg-[#2563EB] text-white text-[10px] font-bold px-2 py-0.5 rounded">
                      <Award size={10} /> Top Rated
                    </span>
                  )}
                  <img src={p.image} alt={p.title} className="w-16 h-16 object-contain" />
                  <span className="text-xs font-semibold text-[#007185] line-clamp-2 max-w-[160px]">{p.title}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="p-3 font-semibold text-[#09090B] border-b border-[#e4e4e7]">Price</td>
            {products.map((p, i) => (
              <td key={i} className={`p-3 text-center border-b border-l border-[#e4e4e7] ${i === bestValue ? "bg-[#fffbe6]" : ""}`}>
                <span className="text-lg font-bold text-[#0f1111]">{p.currency}{p.price}</span>
                {p.originalPrice && (
                  <span className="block text-xs text-[#565959] line-through">{p.currency}{p.originalPrice}</span>
                )}
                {p.discount > 0 && (
                  <span className="inline-block text-[10px] font-bold text-[#cc0c39] mt-0.5">-{p.discount}%</span>
                )}
              </td>
            ))}
          </tr>
          <tr>
            <td className="p-3 font-semibold text-[#09090B] border-b border-[#e4e4e7]">Rating</td>
            {products.map((p, i) => (
              <td key={i} className={`p-3 text-center border-b border-l border-[#e4e4e7] ${i === bestValue ? "bg-[#fffbe6]" : ""}`}>
                <div className="flex justify-center">
                  <StarRating rating={p.rating} compact />
                </div>
              </td>
            ))}
          </tr>
          <tr>
            <td className="p-3 font-semibold text-[#09090B] border-b border-[#e4e4e7]">Reviews</td>
            {products.map((p, i) => (
              <td key={i} className={`p-3 text-center border-b border-l border-[#e4e4e7] text-[#52525B] ${i === bestValue ? "bg-[#fffbe6]" : ""}`}>
                {p.reviews.toLocaleString()}
              </td>
            ))}
          </tr>
          <tr>
            <td className="p-3 font-semibold text-[#09090B] border-b border-[#e4e4e7]">Status</td>
            {products.map((p, i) => {
              const inStock = p.availability === "in_stock";
              return (
                <td key={i} className={`p-3 text-center border-b border-l border-[#e4e4e7] ${i === bestValue ? "bg-[#fffbe6]" : ""}`}>
                  <span className={`font-semibold text-xs ${inStock ? "text-[#067d06]" : "text-[#b12704]"}`}>
                    {inStock ? "In Stock" : "Out of Stock"}
                  </span>
                </td>
              );
            })}
          </tr>
          <tr>
            <td className="p-3 font-semibold text-[#09090B] border-b border-[#e4e4e7]">Features</td>
            {products.map((p, i) => (
              <td key={i} className={`p-3 text-left border-b border-l border-[#e4e4e7] ${i === bestValue ? "bg-[#fffbe6]" : ""}`}>
                <ul className="space-y-1">
                  {p.features.map((f, j) => (
                    <li key={j} className="text-xs text-[#565959] flex items-start gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FF7300] mt-1 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </td>
            ))}
          </tr>
          <tr>
            <td className="p-3 border-[#e4e4e7]"></td>
            {products.map((p, i) => (
              <td key={i} className={`p-3 text-center border-l border-[#e4e4e7] ${i === bestValue ? "bg-[#fffbe6]" : ""}`}>
                <button className="bg-[#ffd814] text-[#0f1111] border border-[#fcd200] rounded-full px-5 py-2 text-xs font-semibold hover:bg-[#f7ca00] transition-colors cursor-pointer">
                  Buy Now
                </button>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      <p className="text-[10px] text-[#888] italic text-center mt-3">
        As an Amazon Associate, we earn from qualifying purchases.
      </p>
    </div>
  );
}

export default function LivePreview() {
  return (
    <section
      id="preview"
      data-testid="preview-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 animate-fade-in-up">
          <span className="inline-flex items-center gap-2 bg-[#FF7300]/10 text-[#FF7300] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            Live Preview
          </span>
          <h2 className="font-display font-extrabold text-[#09090B] text-3xl sm:text-4xl tracking-tight mb-4">
            See What You Get
          </h2>
          <p className="text-[#52525B] text-base md:text-lg max-w-xl mx-auto">
            Preview the exact output the plugin renders on your WordPress frontend.
          </p>
        </div>

        <Tabs defaultValue="cards" className="w-full">
          <div className="flex justify-center mb-10">
            <TabsList className="bg-[#F9FAFB] border border-[#E4E4E7] p-1 rounded-lg" data-testid="preview-tabs">
              <TabsTrigger value="cards" data-testid="tab-cards" className="rounded-md px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Product Cards
              </TabsTrigger>
              <TabsTrigger value="grid" data-testid="tab-grid" className="rounded-md px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Grid Layout
              </TabsTrigger>
              <TabsTrigger value="comparison" data-testid="tab-comparison" className="rounded-md px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5">
                Comparison
                <span className="bg-[#FF7300] text-white text-[9px] font-bold px-1.5 py-0.5 rounded leading-none">PRO</span>
              </TabsTrigger>
              <TabsTrigger value="darkmode" data-testid="tab-darkmode" className="rounded-md px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5">
                Dark Mode
                <span className="bg-[#FF7300] text-white text-[9px] font-bold px-1.5 py-0.5 rounded leading-none">PRO</span>
              </TabsTrigger>
              <TabsTrigger value="shortcodes" data-testid="tab-shortcodes" className="rounded-md px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Shortcodes
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="cards" className="animate-fade-in">
            <div className="flex flex-wrap justify-center gap-6">
              {mockProducts.slice(0, 2).map((p, i) => (
                <div key={i} className="relative">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="grid" className="animate-fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
              {mockProducts.map((p, i) => (
                <div key={i} className="relative">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="comparison" className="animate-fade-in" data-testid="tab-comparison-content">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-[#FF7300]/10 text-[#FF7300] px-3 py-1.5 rounded-full text-xs font-bold mb-3">
                <Crown size={14} /> PRO Feature
              </div>
              <p className="text-sm text-[#52525B] max-w-md mx-auto">
                Auto-generated comparison tables with Best Value and Top Rated badges. Use <code className="bg-[#F4F4F5] px-1.5 py-0.5 rounded text-[11px] font-mono">[affpro_comparison ids="1,2,3"]</code>
              </p>
            </div>
            <ComparisonTable />
          </TabsContent>

          <TabsContent value="darkmode" className="animate-fade-in" data-testid="tab-darkmode-content">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-[#FF7300]/10 text-[#FF7300] px-3 py-1.5 rounded-full text-xs font-bold mb-3">
                <Crown size={14} /> PRO Feature
              </div>
              <p className="text-sm text-[#52525B] max-w-md mx-auto">
                Full dark mode support for product cards. Enable in PRO settings to match your theme.
              </p>
            </div>
            <div className="bg-[#0f0f1a] rounded-xl p-8 max-w-5xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {mockProducts.map((p, i) => (
                  <div key={i} className="relative">
                    <ProductCard product={p} dark />
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="shortcodes" className="animate-fade-in">
            <div className="max-w-2xl mx-auto space-y-6">
              <div>
                <h3 className="font-display font-bold text-[#09090B] text-lg mb-3">Single Product</h3>
                <div className="code-block">
                  <span className="code-keyword">[affiliate_product</span>{" "}
                  <span className="code-attr">id</span>=<span className="code-string">"142"</span>
                  <span className="code-keyword">]</span>
                </div>
                <p className="text-sm text-[#52525B] mt-2">Renders a single product card by WordPress post ID.</p>
              </div>
              <div>
                <h3 className="font-display font-bold text-[#09090B] text-lg mb-3">Product Grid</h3>
                <div className="code-block">
                  <span className="code-keyword">[affiliate_grid</span>{"\n"}
                  {"  "}<span className="code-attr">category</span>=<span className="code-string">"electronics"</span>{"\n"}
                  {"  "}<span className="code-attr">columns</span>=<span className="code-string">"3"</span>{"\n"}
                  {"  "}<span className="code-attr">limit</span>=<span className="code-string">"6"</span>{"\n"}
                  {"  "}<span className="code-attr">orderby</span>=<span className="code-string">"date"</span>
                  <span className="code-keyword">]</span>
                </div>
                <p className="text-sm text-[#52525B] mt-2">Displays a responsive grid of products filtered by category.</p>
              </div>
              <div>
                <h3 className="font-display font-bold text-[#09090B] text-lg mb-3">
                  Comparison Table
                  <span className="ml-2 bg-[#FF7300] text-white text-[10px] font-bold px-2 py-0.5 rounded align-middle">PRO</span>
                </h3>
                <div className="code-block">
                  <span className="code-keyword">[affpro_comparison</span>{"\n"}
                  {"  "}<span className="code-attr">ids</span>=<span className="code-string">"12,45,78"</span>{"\n"}
                  {"  "}<span className="code-attr">sort</span>=<span className="code-string">"price"</span>{"\n"}
                  {"  "}<span className="code-attr">show_features</span>=<span className="code-string">"1"</span>
                  <span className="code-keyword">]</span>
                </div>
                <p className="text-sm text-[#52525B] mt-2">Dynamic comparison table with auto Best Value / Top Rated badges.</p>
              </div>
              <div>
                <h3 className="font-display font-bold text-[#09090B] text-lg mb-3">Gutenberg Block</h3>
                <div className="code-block">
                  <span className="code-comment">{"// Insert via the block editor"}</span>{"\n"}
                  <span className="code-comment">{"// Search for \"Amazon Product\" in the block inserter"}</span>{"\n"}
                  <span className="code-comment">{"// Select a product from the sidebar dropdown"}</span>{"\n"}
                  <span className="code-comment">{"// Preview renders live in the editor"}</span>
                </div>
                <p className="text-sm text-[#52525B] mt-2">Native Gutenberg block with product selector and server-side rendering.</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
